import React from 'react';
import { signInWithRedirect } from 'aws-amplify/auth';
import { Button } from './Button';
import { Rabbit, ShieldCheck } from 'lucide-react';

interface LandingPageProps {
  onSignIn: () => void;
}

const RUTABAGA_HERO_IMG = "/rutabaga.png";

export const LandingPage: React.FC<LandingPageProps> = ({ onSignIn }) => {
  const handleGoogleSignIn = async () => {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('Error signing in with Google:', error);
      // Fall back to showing the Authenticator
      onSignIn();
    }
  };
  return (
    <div className="min-h-screen flex flex-col bg-rutabaga-bg relative overflow-hidden">
      
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-rutabaga-pink/20 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-rutabaga-wood/10 rounded-full filter blur-3xl translate-x-1/3 translate-y-1/3"></div>

      {/* Nav */}
      <nav className="relative z-10 w-full p-6 flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
           <div className="w-8 h-8 bg-rutabaga-text rounded-full flex items-center justify-center">
              <Rabbit className="text-white w-5 h-5" />
           </div>
           <span className="font-heading font-bold text-lg text-rutabaga-text">Baga<span className="text-rutabaga-wood">Cam</span></span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleGoogleSignIn}>Login</Button>
      </nav>

      {/* Main Content */}
      <main className="flex-grow flex flex-col items-center justify-center px-4 sm:px-6 relative z-10 pb-16 pt-8">
        <div className="text-center max-w-4xl mx-auto space-y-8 animate-in slide-in-from-bottom-8 duration-700 fade-in">
          
          <h1 className="font-heading font-extrabold text-5xl sm:text-6xl md:text-7xl text-rutabaga-text tracking-tight leading-tight">
            Check in on <br/>
            <span className="text-rutabaga-wood">the bun</span>, 24/7.
          </h1>
          
          <p className="text-xl sm:text-2xl text-gray-500 font-light max-w-lg mx-auto">
            A cozy window into Baga's world. <br/> Watch him hop, flop, and monch.
          </p>

          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4 relative z-20">
            <Button size="lg" onClick={handleGoogleSignIn} className="w-full sm:w-auto min-w-[200px] shadow-xl hover:shadow-2xl hover:-translate-y-1">
              <span className="mr-2">G</span> Sign in with Google
            </Button>
          </div>

          {/* Hero Image - Updated for Transparent PNG */}
          <div className="relative mt-8 mx-auto max-w-lg flex justify-center items-end min-h-[400px]">
            {/* Glow behind the bunny */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[300px] h-[300px] bg-gradient-to-t from-rutabaga-wood/30 to-transparent rounded-full filter blur-2xl"></div>
            
            {/* The Bunny */}
            <img
              src={RUTABAGA_HERO_IMG}
              alt="Baga the rabbit"
              className="relative z-10 w-auto h-auto max-h-[450px] drop-shadow-2xl hover:scale-105 transition-transform duration-500 ease-in-out"
            />
          </div>

          <div className="pt-8 flex items-center justify-center gap-6 text-sm text-gray-400">
            <div className="flex items-center gap-1">
              <ShieldCheck className="w-4 h-4" />
              <span>Secure Stream</span>
            </div>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <div>Private Access</div>
            <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
            <div>100% Cute</div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-6 text-center text-gray-400 text-sm">
        &copy; 2026 BagaCam. Made with 🥕.
      </footer>
    </div>
  );
};